/*
  * Copyright Mission 42 Ventures, Inc. All rights reserved.
  * Unauthorized copying, distribution, or use is prohibited.
  */

import { useCallback, useMemo } from 'react';
import { discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { AuthzBoard, AuthzServiceNode } from './types';

/**
 * The board is served by the network-policy backend plugin: it is built in the
 * same rebuild pass off the same fetched files, so it shares that plugin's id
 * and its /access gate rather than standing up a second one.
 */
export const useAuthorizationPolicyApi = () => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const call = useCallback(
    async (path: string) => {
      const base = await discoveryApi.getBaseUrl('network-policy');
      const res = await fetchApi.fetch(`${base}${path}`);
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body.error ?? `${res.status} ${res.statusText}`);
      }
      return body;
    },
    [discoveryApi, fetchApi],
  );

  const getBoard = useCallback(
    (refresh = false): Promise<AuthzBoard> =>
      call(`/authorization/board${refresh ? '?refresh=true' : ''}`),
    [call],
  );

  const getService = useCallback(
    (name: string): Promise<AuthzServiceNode> =>
      call(`/authorization/service/${encodeURIComponent(name)}`),
    [call],
  );

  // Stable identity: consumers hang effects off this object (same convention
  // as useNetworkPolicyApi).
  return useMemo(() => ({ getBoard, getService }), [getBoard, getService]);
};
